/**
 * RSVP (Rapid Serial Visual Presentation) utility functions
 * These functions handle the core RSVP reading logic including
 * calculating the optimal recognition point (ORP) and word delays.
 */

export interface ORPResult {
  before: string;
  focus: string;
  after: string;
}

/**
 * Calculate the Optimal Recognition Point (ORP) for a word.
 *
 * The ORP is the character position within a word where the human eye
 * naturally fixates for fastest word recognition. This implementation
 * uses a hybrid approach based on published academic research:
 *
 * - For words ≤7 letters: 30% from left (McConkie & Rayner, 1975)
 * - For words >7 letters: 40% from left (O'Regan, 1990)
 *
 * This research predates Spritz, Inc.'s patent filings by 20-40 years.
 * This implementation was independently developed and is not derived from
 * Spritz's proprietary technology or patents.
 *
 * @see McConkie, G. W., & Rayner, K. (1975). Vision Research, 15(6), 731-739.
 * @see O'Regan, J. K. (1990). Eye Movements and Their Role in Visual and Cognitive Processes.
 *
 * @param word - The word to calculate the ORP for
 * @returns Object containing the word split into before, focus (ORP), and after parts
 */
export function calculateORP(word: string): ORPResult {
  const length = word.length;
  
  if (length === 0) {
    return { before: '', focus: '', after: '' };
  }
  
  // Hybrid approach: 30% for short words (≤7), 40% for longer words (>7)
  // This balances research-backed PVL positioning with visual clarity
  const orpIndex = Math.max(1, Math.floor(length * (length <= 7 ? 0.3 : 0.4)));
  
  return {
    before: word.slice(0, orpIndex),
    focus: word[orpIndex],
    after: word.slice(orpIndex + 1)
  };
}

/**
 * Calculate the display delay for a word based on WPM and word complexity.
 * 
 * @param wpm - Words per minute setting
 * @param word - The word to calculate delay for
 * @returns Delay in milliseconds
 */
export function calculateWordDelay(wpm: number, word: string): number {
  // Base delay from WPM
  const baseDelay = 60000 / wpm;
  
  // Adjust for word length (longer words need more time)
  const length = word.length;
  
  let lengthMultiplier = 1;
  
  if (length <= 3) {
    lengthMultiplier = 0.8;
  } else if (length <= 5) {
    lengthMultiplier = 1.0;
  } else if (length <= 8) {
    lengthMultiplier = 1.2;
  } else if (length <= 12) {
    lengthMultiplier = 1.4;
  } else {
    lengthMultiplier = 1.6;
  }
  
  // Adjust for punctuation (pauses for sentence ends)
  let punctuationMultiplier = 1;
  if (/[.!?]$/.test(word)) {
    punctuationMultiplier = 1.5;
  } else if (/[,;:]$/.test(word)) {
    punctuationMultiplier = 1.2;
  }
  
  return baseDelay * lengthMultiplier * punctuationMultiplier;
}

/**
 * Calculate the estimated total reading time for content.
 * 
 * @param wordCount - Number of words in the content
 * @param wpm - Words per minute setting
 * @returns Estimated time in seconds
 */
export function calculateReadingTime(wordCount: number, wpm: number): number {
  return Math.ceil((wordCount / wpm) * 60);
}
